import { useCallback, useState } from 'react';

import { createPortalSession } from 'src/api/functions/subscriptionApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { config } from 'src/utils/config';
import { useAccessToken } from './useAccessToken';

/**
 * Stripe Billing Customer Portal (hosted redirect).
 *
 * `openPaymentManagement()` asks the backend for a portal session URL
 * (`POST /api/v1/billing/payment-session`) and redirects the browser to
 * Stripe's hosted portal, scoped (in the Stripe dashboard config) to
 * payment-method management + invoice history. Card data never touches our
 * page — Stripe handles it (PCI). On finishing, the portal returns the user to
 * this page.
 *
 * `enabled` is false when Stripe isn't configured on the frontend
 * (`REACT_APP_STRIPE_PUBLISHABLE_KEY` unset).
 */
function currentReturnUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin + window.location.pathname;
}

export function useStripePayment() {
  const accessToken = useAccessToken();
  const enabled = Boolean(config.stripePublishableKey);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPaymentManagement = useCallback(async () => {
    if (!enabled || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await createPortalSession(currentReturnUrl(), accessToken);
      if (isErrorResponseResult(result)) {
        setError('payment_unavailable');
        return;
      }
      const session = result as unknown as { url: string };
      if (!session.url) {
        setError('payment_unavailable');
        return;
      }
      window.location.assign(session.url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Stripe] portal error', e);
      setError('payment_error');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, enabled, isLoading]);

  return { enabled, isLoading, error, openPaymentManagement };
}
